// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

// Import all field components from the index file
import {
  TextFieldComponent,
  IntegerFieldComponent,
  DecimalFieldComponent,
  ChecklistFieldComponent,
  DateFieldComponent,
  DropdownFieldComponent,
  MultiSelectFieldComponent,
  CanvasFieldRenderer,
  UserSelectFieldComponent,
  GroupSelectFieldComponent,
  AssociationFieldComponent,
  DocumentFieldComponent,
  MultiDocumentFieldComponent,
  TableFieldComponent,
  IframeFieldComponent,
  TextFieldExtendedComponent,
  FieldChangeActionsComponent,
  ParameterTableFieldComponent,
  AttributeListFieldComponent,
  EmailTemplateListFieldComponent,
  TemplateListFieldComponent,
  AdvancedTableRenderer,
  ParagraphFieldRenderer,
  InventoryViewAttribute,
  AssociationFieldTableViewComponent,
} from './FieldComponents';
import UserMultiSelectFieldComponent from './FieldComponents/UserMultiSelectFieldComponent';
import GroupMultiSelectFieldComponent from './FieldComponents/GroupMultiSelectFieldComponent';
import { checkAttributeIsRequired } from '../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../const/CommonConst';
import { useGroupsQuery } from '../../../features/administrativeOptions/userManagement/api/UserManagementQueries';

/**
 * A reusable component that renders the appropriate field based on attribute type
 * Can be used in any context where attribute fields need to be rendered
 * @param {Object} props - Component props
 * @param {Object} props.attribute - The attribute object with metadata (type, name, etc.)
 * @param {any} props.value - Current value of the attribute
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Array} props.usersList - List of users (for User type)
 * @returns {React.ReactElement} The appropriate field component
 */
const AttributeField = ({
  attribute,
  value,
  onChange,
  usersList = EMPTY_ARRAY,
  entityData = EMPTY_OBJECT,
  entityDetailsFormikForm,
  onCalculateAttribute,
  onFileStatusChange,
  groupDetails = EMPTY_OBJECT,
  modelTemplateDetails = EMPTY_OBJECT,
}) => {
  /**
   * Renders the appropriate input field based on the attribute's data type
   * @function
   * @returns {React.ReactElement} The rendered input field
   */
  const isDerived = useMemo(() => {
    return attribute?.is_derived;
  }, [attribute]);
  const { data: groupList = EMPTY_ARRAY } = useGroupsQuery();

  const renderField = useCallback(() => {
    if (!attribute) return null;

    const isLongLabel = attribute?.label?.length > 90;

    // Check if field is required based on yup_schema

    const isRequired = checkAttributeIsRequired(attribute);

    const fieldProps = {
      label: isLongLabel
        ? attribute?.label?.slice(0, 90) + '...'
        : attribute?.label,
      titleLabel: attribute?.label,
      value,
      onChange,
      attribute,
      entityDetailsFormikForm,
      required: isRequired, // Add required prop
      modelTemplateDetails,
    };

    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;

    if (dataType === 'Character') {
      switch (inputType) {
        case 'TEXTFIELD':
          return <TextFieldComponent {...fieldProps} />;
        case 'SINGLE_SELECT':
          return <DropdownFieldComponent {...fieldProps} />;
        case 'MULTI_SELECT':
          return <MultiSelectFieldComponent {...fieldProps} />;
        case 'TEXTFIELD_EXTENDED':
          return <TextFieldExtendedComponent {...fieldProps} />;
        case 'Canvas':
          return <CanvasFieldRenderer {...fieldProps} />;
        default:
          return <TextFieldComponent {...fieldProps} />;
      }
    }

    if (dataType === 'User' && inputType === 'MULTI_SELECT') {
      return (
        <UserMultiSelectFieldComponent
          {...fieldProps}
          usersList={usersList}
          formData={entityDetailsFormikForm?.values || {}}
          entityData={entityData}
          loader={true}
        />
      );
    }

    if (dataType === 'Group' && inputType === 'MULTI_SELECT') {
      return (
        <GroupMultiSelectFieldComponent {...fieldProps} groupList={groupList} />
      );
    }

    // Then check data types
    switch (dataType) {
      case 'Integer':
        return (
          <IntegerFieldComponent
            {...fieldProps}
            isDerived={isDerived}
            handleOpenDerivedModal={onCalculateAttribute}
          />
        );
      case 'Decimal':
        return (
          <DecimalFieldComponent
            {...fieldProps}
            isDerived={isDerived}
            handleOpenDerivedModal={onCalculateAttribute}
          />
        );
      case 'Date':
        return (
          <DateFieldComponent
            {...fieldProps}
            isDerived={isDerived}
            handleOpenDerivedModal={onCalculateAttribute}
          />
        );
      case 'User':
        return (
          <UserSelectFieldComponent
            {...fieldProps}
            usersList={usersList}
            groupDetails={groupDetails}
            formData={entityDetailsFormikForm?.values || {}}
            entityData={entityData}
            loader={true}
          />
        );
      case 'Group':
        return (
          <GroupSelectFieldComponent {...fieldProps} groupList={groupList} />
        );
      case 'Association':
        if (attribute?.show_as_table) {
          return (
            <AssociationFieldTableViewComponent
              attribute={attribute}
              entityId={entityData?.entity_id}
              entityData={entityData}
            />
          );
        }
        return (
          <AssociationFieldComponent
            {...fieldProps}
            formData={entityDetailsFormikForm?.values || {}}
            entityData={entityData}
            loader={true}
          />
        );
      case 'AttributeList':
        return <AttributeListFieldComponent {...fieldProps} />;
      case 'ParameterTableList':
        return <ParameterTableFieldComponent {...fieldProps} />;
      case 'EmailTemplateList':
        return <EmailTemplateListFieldComponent {...fieldProps} />;
      case 'TemplateList':
        return <TemplateListFieldComponent {...fieldProps} />;
      case 'FieldChangeActions':
        return <FieldChangeActionsComponent {...fieldProps} />;
      case 'InventoryView':
        return (
          <InventoryViewAttribute
            {...fieldProps}
            entityId={entityData?.entity_id}
          />
        );
      // To Do: Need to implement this in new way as previously we were linking the doc and attribute, from BE as well
      case 'Document':
        if (attribute?.input_control_type === 'MULTI_DOCUMENT') {
          return (
            <MultiDocumentFieldComponent
              {...fieldProps}
              entityData={entityData}
              onFileStatusChange={onFileStatusChange}
            />
          );
        }
        return (
          <DocumentFieldComponent
            {...fieldProps}
            entityData={entityData}
            onFileStatusChange={onFileStatusChange}
          />
        );
      case 'Chart':
        return <IframeFieldComponent {...fieldProps} />;
      case 'Table':
        return <TableFieldComponent {...fieldProps} />;
      case 'Hyperlink':
        return <TextFieldComponent {...fieldProps} />;
      case 'Checklist':
        return (
          <ChecklistFieldComponent
            {...fieldProps}
            entityId={entityData?.entity_id}
            entityType={entityData?.entity_type}
          />
        );
      case 'AdvancedTable':
        return (
          <AdvancedTableRenderer
            {...fieldProps}
            isViewMode={false}
            entityData={entityData}
            usersList={usersList}
          />
        );
      case 'Paragraph':
        return <ParagraphFieldRenderer {...fieldProps} />;
      default:
        return (
          <TextFieldComponent
            {...fieldProps}
            isDerived={isDerived}
            handleOpenDerivedModal={onCalculateAttribute}
          />
        );
    }
  }, [
    attribute,
    value,
    onChange,
    usersList,
    groupList,
    entityData,
    entityDetailsFormikForm,
  ]);

  return renderField();
};

AttributeField.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  onChange: PropTypes.func.isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  dropdownOptions: PropTypes.arrayOf(PropTypes.shape({})),
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
};

export default AttributeField;
